import React, { useRef } from 'react';
import {
  motion,
  useInView,
  useScroll,
  useTransform,
  useSpring,
} from 'framer-motion';
import {
  Car,
  Calendar,
  ClipboardList,
  Building2,
  ArrowRight,
  Smartphone,
  ShieldCheck,
  Navigation,
  Wallet,
  Star,
} from 'lucide-react';
import './OurServices.css';

const EASE = [0.22, 1, 0.36, 1];

const SERVICES = [
  { title: 'Hourly Driver', tagline: 'Book by the hour', icon: Car, hue: 'a' },
  { title: 'Daily Driver', tagline: 'Full-day service', icon: Calendar, hue: 'b' },
  { title: 'Weekly Driver', tagline: 'Weekly commute', icon: ClipboardList, hue: 'c' },
  { title: 'Monthly Driver', tagline: 'Monthly plan', icon: Building2, hue: 'd' },
  { title: 'One Way Driver', tagline: 'Single destination', icon: ArrowRight, hue: 'e' },
];

const WORKFLOW = [
  { num: '01', label: 'Book', icon: Smartphone },
  { num: '02', label: 'Match', icon: ShieldCheck },
  { num: '03', label: 'Track', icon: Navigation },
  { num: '04', label: 'Pickup', icon: Car },
  { num: '05', label: 'Ride', icon: Wallet },
  { num: '06', label: 'Rate', icon: Star },
];

const PROGRESS_PATH =
  'M 40 280 C 140 260, 220 220, 320 190 S 520 110, 680 80 S 860 45, 980 28';

const fadeUp = {
  hidden: { opacity: 0, y: 28, filter: 'blur(6px)' },
  visible: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: { duration: 0.65, ease: EASE },
  },
};

const zoneStagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1, delayChildren: 0.05 },
  },
};

const cardVariant = {
  hidden: { opacity: 0, y: 36, scale: 0.92 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.55, delay: 0.2 + i * 0.1, ease: EASE },
  }),
};

function ServiceCard({ service, index }) {
  const Icon = service.icon;

  return (
    <motion.article
      className={`os-card os-card--${service.hue}`}
      custom={index}
      variants={cardVariant}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.4 }}
      whileHover={{ y: -6, scale: 1.025 }}
      whileTap={{ scale: 0.98 }}
    >
      <span className="os-card-shine" aria-hidden="true" />
      <motion.div
        className="os-card-icon"
        whileHover={{ rotate: [0, -8, 8, 0], scale: 1.08 }}
        transition={{ duration: 0.45 }}
      >
        <Icon size={23} strokeWidth={2} aria-hidden="true" />
      </motion.div>
      <h3 className="os-card-title">{service.title}</h3>
      <p className="os-card-tag">{service.tagline}</p>
    </motion.article>
  );
}

function ProgressGraph({ progress }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  const pathDraw = useTransform(progress, [0.2, 0.75], [0, 1]);
  const smoothDraw = useSpring(pathDraw, { stiffness: 60, damping: 20 });

  return (
    <div className="os-graph" aria-label="How idrivea works" ref={ref}>
      <div className="os-graph-main">
        <div className="os-graph-grid" aria-hidden="true">
          {[75, 50, 25].map((pct) => (
            <span key={pct} className="os-grid-line" style={{ bottom: `${pct}%` }} />
          ))}
        </div>

        <svg
          className="os-graph-svg"
          viewBox="0 0 1020 300"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path d={PROGRESS_PATH} className="os-graph-path-bg" fill="none" />
          <motion.path
            d={PROGRESS_PATH}
            className="os-graph-path"
            fill="none"
            style={{ pathLength: smoothDraw }}
          />
        </svg>

        <ol className="os-graph-steps">
          {WORKFLOW.map(({ num, label, icon: Icon }, index) => (
            <motion.li
              key={num}
              className="os-graph-step"
              initial={{ opacity: 0, scale: 0.5 }}
              animate={inView ? { opacity: 1, scale: 1 } : {}}
              transition={{ delay: 0.35 + index * 0.12, duration: 0.5, ease: EASE }}
            >
              <motion.div
                className="os-step-node"
                animate={inView ? { y: [0, -4, 0] } : {}}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  delay: index * 0.35,
                  ease: 'easeInOut',
                }}
                whileHover={{ scale: 1.12, rotate: 6 }}
              >
                <span className="os-step-num">{num}</span>
                <Icon size={18} strokeWidth={2} aria-hidden="true" />
                <span className="os-step-ring" aria-hidden="true" />
              </motion.div>
              <span className="os-step-label">{label}</span>
            </motion.li>
          ))}
        </ol>
      </div>

      <motion.div
        className="os-graph-x"
        initial={{ opacity: 0, y: 10 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ delay: 0.9, duration: 0.5, ease: EASE }}
      >
        <span>Your Journey</span>
        <motion.span
          className="os-graph-arrow"
          animate={{ x: [0, 6, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
        >
          →
        </motion.span>
      </motion.div>
    </div>
  );
}

export default function OurServices() {
  const sectionRef = useRef(null);
  const inView = useInView(sectionRef, { once: true, amount: 0.12 });

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });
  const smoothProgress = useSpring(scrollYProgress, { stiffness: 80, damping: 28 });

  return (
    <section
      id="services"
      className="os-section"
      ref={sectionRef}
      aria-labelledby="services-heading"
    >
      <div className="os-map-wrap">
        <div className="os-map-ui">
          <div className="os-zone os-zone--services">
            <div className="os-map-bg-wrap" aria-hidden="true">
              <img src="/map.png" alt="" className="os-map-bg" />
            </div>
            <motion.div
              className="os-zone-inner"
              variants={zoneStagger}
              initial="hidden"
              animate={inView ? 'visible' : 'hidden'}
            >
            <motion.span className="os-badge" variants={fadeUp}>
              What we offer
            </motion.span>
            <motion.h2
              id="services-heading"
              className="os-title"
              variants={fadeUp}
            >
              Our <span className="os-accent">Services</span>
            </motion.h2>
            <motion.p className="os-subtitle" variants={fadeUp}>
              Choose your driver in seconds.
            </motion.p>

            <div className="os-cards-wrap">
              <div className="os-cards">
                {SERVICES.map((service, index) => (
                  <ServiceCard key={service.title} service={service} index={index} />
                ))}
              </div>
            </div>
            </motion.div>
          </div>

          <motion.div
            id="how-it-works"
            className="os-zone os-zone--graph"
            variants={zoneStagger}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
          >
            <motion.span className="os-badge os-badge--works" variants={fadeUp}>
              Step by step
            </motion.span>
            <motion.h2 className="os-title" variants={fadeUp}>
              How i<span className="os-accent">DRIVEA</span> Works
            </motion.h2>
            <motion.p className="os-subtitle" variants={fadeUp}>
              Simple. Safe. Reliable.
            </motion.p>

            <motion.div variants={fadeUp}>
              <ProgressGraph progress={smoothProgress} />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
